import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaFileWord,
  FaFilePdf,
  FaFileCsv,
  FaFileAlt,
  FaRegTrashAlt,
} from "react-icons/fa";
import DragAndDropDocument from "../../../../../../components/DocumentUpload/upload";
import moment from "moment";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const DocumentsComponent = ({
  items,
  mediaData,
  handleMediaDelete,
  handleCreateMedia,
}) => {
  const [uploadedDocument, setUploadedDocument] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    setUploadedDocument(mediaData || []);
  }, [mediaData]);

  const handleDocumentsUpdate = (images) => {
    setUploadedDocument(images);
    handleCreateMedia(images);
  };

  const handleCheckboxChange = (file) => {
    setSelectedFiles((prevSelected) =>
      prevSelected.includes(file)
        ? prevSelected.filter((item) => item !== file)
        : [...prevSelected, file]
    );
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(filteredMediaData);
    }
    setSelectAll(!selectAll);
  };

  const handleDownloadSelected = async () => {
    if (selectedFiles.length === 0) return;

    // Single file download: download file directly
    if (selectedFiles.length === 1) {
      const doc = selectedFiles[0];
      try {
        const response = await fetch(doc.url);
        const blob = await response.blob();
        saveAs(blob, doc.name || "document");
      } catch (error) {
        console.error("Error downloading file:", error);
        alert("Error downloading file.");
      }
      return;
    }

    // Multiple files: download as a zip archive
    const zip = new JSZip();
    const folder = zip.folder("Selected_Documents");

    try {
      await Promise.all(
        selectedFiles.map(async (doc) => {
          if (doc.url) {
            const response = await fetch(doc.url);
            const blob = await response.blob();
            folder.file(doc.name || "document", blob);
          }
        })
      );
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "Selected_Documents.zip");
    } catch (error) {
      console.error("Error downloading files:", error);
      alert("Error downloading files.");
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    if (ext === "docx" || ext === "doc")
      return <FaFileWord className="text-blue-500 text-xl" />;
    if (ext === "pdf") return <FaFilePdf className="text-red-500 text-xl" />;
    if (ext === "csv") return <FaFileCsv className="text-green-500 text-xl" />;
    return <FaFileAlt className="text-gray-500 text-xl" />;
  };

  const filteredMediaData = mediaData?.filter((doc) => {
    const ext = doc.name?.split(".").pop().toLowerCase();
    return ["docx", "doc", "pdf", "csv"].includes(ext);
  });

  useEffect(() => {
    setSelectedFiles((prev) => {
      const newSelectedFiles = prev.filter((file) =>
        filteredMediaData.some((doc) => doc.id === file.id)
      );
      return prev.length !== newSelectedFiles.length ? newSelectedFiles : prev;
    });
  }, [filteredMediaData]);

  useEffect(() => {
    const newSelectAll =
      selectedFiles.length === filteredMediaData.length &&
      filteredMediaData.length > 0;
    if (selectAll !== newSelectAll) {
      setSelectAll(newSelectAll);
    }
  }, [selectedFiles, filteredMediaData]);

  return (
    <div className="border border-lightPrimary/40 dark:border-info/40 min-h-[53vh] p-3">
      <div className="flex mb-4 md:gap-6 gap-2 items-center">
        <DragAndDropDocument
          onImagesUpdate={handleDocumentsUpdate}
          handleMediaDelete={handleMediaDelete}
          uploadedImages={uploadedDocument}
          accept=".csv,.docx,.doc,.pdf"
          showPic={3}
        />
        <button
          onClick={handleDownloadSelected}
          disabled={selectedFiles.length === 0}
          className={`px-4 py-[7px] font-medium text-sm rounded-md ${
            selectedFiles.length > 0
              ? "cursor-pointer dark:text-black dark:button-primary-gradient bg-[radial-gradient(circle_at_10%_10%,rgb(237,237,237),#9e9e9e,#9e9e9e,#9e9e9e)] text-dark dark:button-primary-gradient"
              : "bg-gray-400 text-gray-700 opacity-50 cursor-not-allowed"
          }`}
        >
          Download
        </button>
      </div>

      <div className="w-full overflow-x-auto">
        <table className="min-w-[600px] w-full border-collapse border-t border-transparent text-left">
          <thead>
            <tr className="border-b border-lightPrimary/40 dark:border-info/40 font-normal text-lightPrimary dark:text-primary-gradient">
              {["", "File", "Related Item", "Preview", "Date", "Action"].map(
                (header, index) => (
                  <th
                    key={index}
                    className="py-2 px-4 whitespace-nowrap text-xl font-normal"
                  >
                    {index === 0 ? (
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAllChange}
                        className="cursor-pointer"
                      />
                    ) : (
                      header
                    )}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {filteredMediaData?.length > 0 ? (
              filteredMediaData.map((doc, index) => (
                <tr
                  key={index}
                  className="border-b border-transparent text-lightPrimary dark:text-primary-gradient"
                >
                  <td className="py-2 px-4">
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(doc)}
                      onChange={() => handleCheckboxChange(doc)}
                      className="cursor-pointer"
                    />
                  </td>
                  <td className="py-2 px-4 flex items-center gap-2 whitespace-nowrap">
                    {getFileIcon(doc.name)}
                    {doc.name ? (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        {doc.name}
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td className="py-2 px-4 whitespace-nowrap">
                    {items?.name || "N/A"}
                  </td>
                  <td className="py-2 px-4 text-center">
                    {doc.url ? (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FaEye className="dark:text-grey text-primary cursor-pointer" />
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td className="py-2 px-4 whitespace-nowrap">
                    {doc.created_at
                      ? moment(doc.created_at).format("MM/DD/YYYY")
                      : "N/A"}
                  </td>
                  <td className="py-2 px-4 text-center">
                    <FaRegTrashAlt
                      className="text-darkWaring cursor-pointer hover:text-red-700"
                      onClick={() => {
                        handleMediaDelete(doc.id, "File");
                      }}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="py-4 text-gray-500">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DocumentsComponent;
